// Remove a Wi-Fi network from a device.
//
// Ports wifiHandler.dart removeWifiPopup: list what the device is holding, tap
// one, confirm, publish REMOVEWIFI.
//
// The list comes from the device itself. `getallwifi` makes it report every
// network it holds on its /client topic — there is no server copy to read, and
// removing a network the device does not have would be a no-op the user could
// not tell apart from a failure.
//
// ⚠️ THE REMOVE GOES OUT WITHOUT `#<userId>`. On the ESP8266 that suffix lands
// inside the password, and `SMARTPicker::removeSsid` needs the password to match
// exactly — so nothing was ever erased while the device rebooted anyway, which
// is what made this look like it worked. `publishAddressed`, not
// `publishCommand`; the firmware quote is in core/devices/commands.js.
//
// ⚠️ AND NOTHING IS CROSSED OFF UNTIL THE DEVICE SAYS SO. This used to drop the
// row from the list the moment Remove was tapped, so every removal looked
// successful — including the ones that were not happening at all.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  List, ListItemButton, ListItemText, ListItemIcon, CircularProgress, useTheme,
} from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import LockIcon from '@mui/icons-material/Lock';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useApp } from '../../../core/state/AppContext';
import { publishCommand, publishAddressed } from '../../../core/utils/deviceController';
import { connectMqtt, subscribeTopic, addMessageListener } from '../../../core/mqtt/mqttClient';
import {
  CMD_GET_ALL_WIFI, buildRemoveWifiMqtt, parseWifiList, parseWifiAck, isProtectedSsid,
  WIFI_ACK_OK, WIFI_ACK_NOT_FOUND, WIFI_ACK_ERROR, WIFI_REBOOT_SETTLE_MS,
} from '../../../core/devices/commands';

const LIST_TIMEOUT_MS = 6000;

// `EMPTY` is an answer, not silence — see DeviceWifiPanel for the same guard.
const SAYS_EMPTY = (payload) => /^(\[[\w-]{1,32}\]\s*)?EMPTY$/i.test(String(payload).trim());

export default function WifiRemoveDialog({ open, uno, onClose }) {
  const theme = useTheme();
  const c = theme.sli;
  const { userId } = useApp();

  const [list, setList] = useState(null);   // null = still waiting
  const [confirm, setConfirm] = useState(null);
  const [note, setNote] = useState(null);   // { tone: 'bad'|'ok'|'wait', text }

  const awaitingRef = useRef(false);
  const pendingRef = useRef(null);          // ssid a REMOVEWIFI ack is due for
  const listTimer = useRef(null);
  const settleTimer = useRef(null);

  const ask = useCallback(() => {
    setList(null);
    awaitingRef.current = true;
    publishCommand(uno, CMD_GET_ALL_WIFI, userId);
    clearTimeout(listTimer.current);
    // A device that never answers leaves an honest empty list rather than a
    // spinner that never stops.
    listTimer.current = setTimeout(() => {
      if (!awaitingRef.current) return;
      awaitingRef.current = false;
      setList([]);
    }, LIST_TIMEOUT_MS);
  }, [uno, userId]);

  useEffect(() => {
    if (!open || !uno) return undefined;
    setConfirm(null);
    setNote(null);
    pendingRef.current = null;

    connectMqtt();
    subscribeTopic(`${uno}/client`);

    // Listen before asking: the reply can arrive in milliseconds.
    const detach = addMessageListener((topic, payload) => {
      if (topic !== `${uno}/client`) return;

      const ack = parseWifiAck(payload);
      if (ack && ack.verb === 'REMOVEWIFI' && pendingRef.current) {
        const name = ack.ssid || pendingRef.current;
        pendingRef.current = null;
        if (ack.result === WIFI_ACK_NOT_FOUND) {
          clearTimeout(settleTimer.current);
          setNote({ tone: 'bad', text: `The device does not hold "${name}" — nothing was removed.` });
          return;
        }
        if (ack.result === WIFI_ACK_ERROR) {
          clearTimeout(settleTimer.current);
          setNote({ tone: 'bad', text: ack.text });
          return;
        }
        setNote({
          tone: ack.result === WIFI_ACK_OK ? 'ok' : 'wait',
          text: ack.result === WIFI_ACK_OK
            ? `Removed "${name}". The device is restarting — re-reading its list.`
            : 'The device accepted it and is restarting. Re-reading its list.',
        });
        return;
      }

      if (!awaitingRef.current) return;
      const parsed = parseWifiList(payload);
      if (!parsed.length && !SAYS_EMPTY(payload)) return;
      awaitingRef.current = false;
      clearTimeout(listTimer.current);
      setList(parsed);
    });

    ask();

    return () => {
      detach();
      clearTimeout(listTimer.current);
      clearTimeout(settleTimer.current);
    };
  }, [open, uno, ask]);

  const tap = (w) => {
    if (isProtectedSsid(w.ssid)) {
      setNote({
        tone: 'bad',
        text: `"${w.ssid}" is a fallback network — removing it could leave the device`
          + ' with no way back online.',
      });
      return;
    }
    setNote(null);
    setConfirm(w);
  };

  const doRemove = () => {
    pendingRef.current = confirm.ssid;
    publishAddressed(uno, buildRemoveWifiMqtt(confirm.ssid, confirm.pass));
    setNote({ tone: 'wait', text: `Removing "${confirm.ssid}" — waiting for the device.` });
    setConfirm(null);
    // The device restarts on a successful removal, so the list can only be
    // re-read once it is back. This runs whether or not an ack arrived: a
    // firmware that says nothing still has to be re-checked, not assumed.
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(ask, WIFI_REBOOT_SETTLE_MS);
  };

  const paper = { bgcolor: c.back, borderRadius: '20px', width: 'min(92vw, 420px)' };
  const noteColor = (tone) => (tone === 'bad' ? '#F44336' : tone === 'ok' ? '#4CAF50' : `${c.text}B3`);

  return (
    <>
      <Dialog open={open} onClose={onClose} PaperProps={{ sx: paper }}>
        <DialogTitle sx={{ color: c.text, fontWeight: 700, fontSize: 18 }}>
          Connected Wi-Fi
        </DialogTitle>
        <DialogContent>
          {list === null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
              <CircularProgress size={18} sx={{ color: c.text }} />
              <Typography sx={{ color: c.text }}>Asking the device…</Typography>
            </Box>
          ) : list.length === 0 ? (
            <Typography sx={{ color: `${c.text}B3` }}>
              The device did not answer, or holds no networks. If it was just
              restarted after a change, give it a few seconds and reopen this.
            </Typography>
          ) : (
            <List dense disablePadding sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {list.map((w) => (
                <ListItemButton key={w.ssid} onClick={() => tap(w)} sx={{ borderRadius: '12px' }}>
                  <ListItemIcon sx={{ color: c.text, minWidth: 34 }}>
                    {isProtectedSsid(w.ssid) ? <LockIcon /> : <WifiIcon />}
                  </ListItemIcon>
                  <ListItemText
                    primary={w.ssid}
                    primaryTypographyProps={{ sx: { color: c.text, fontWeight: 600 } }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
          {note && (
            <Typography sx={{ color: noteColor(note.tone), fontSize: 13, mt: 1 }}>
              {note.text}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ color: c.text }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} PaperProps={{ sx: paper }}>
        <DialogContent sx={{ textAlign: 'center', pt: 3 }}>
          <WarningAmberIcon sx={{ fontSize: 48, color: '#F44336' }} />
          <Typography sx={{ color: c.text, fontSize: 20, fontWeight: 700, mt: 1 }}>
            Remove Wi-Fi?
          </Typography>
          <Typography sx={{ color: `${c.text}B3`, mt: 1 }}>
            {`Remove "${confirm?.ssid}" from this device?`}
          </Typography>
          <Typography sx={{ color: `${c.text}80`, fontSize: 12, mt: 1 }}>
            The device restarts to apply it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} sx={{ color: c.text }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={doRemove}
            sx={{ bgcolor: '#F44336', color: '#fff', borderRadius: '12px' }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
